import { useEffect, useRef } from 'react';
import { usePlatformTheme } from '@/hooks/usePlatformTheme';

const PASSO = 44;
const RAIO = 190;

/**
 * Fundo das telas de entrada: uma malha de cruzes finas no verde da marca.
 *
 * Portado do "campo" do site do DevClub (`devclub-lp/parts/js/00c-campo.js`):
 * perto do mouse as cruzes acendem, crescem, giram 45° e se afastam um pouco,
 * como limalha num ímã; na rolagem a malha escorrega mais devagar que a página.
 * Não é loop: o quadro só roda enquanto o mouse ou a rolagem estão assentando
 * e para sozinho. Com movimento reduzido ou no toque, a malha fica parada.
 */
export function CampoDeCruzes() {
  const ref = useRef<HTMLCanvasElement>(null);
  const { theme } = usePlatformTheme();

  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const claro = theme === 'light';

    const quieto = matchMedia('(prefers-reduced-motion: reduce)');
    const fino = matchMedia('(hover: hover) and (pointer: fine)');
    let w = 0;
    let h = 0;
    let dpr = 1;
    let mx = -9999;
    let my = -9999;
    let ax = -9999;
    let ay = -9999;
    let forca = 0;
    let alvoForca = 0;
    let deriva = 0;
    let alvoDeriva = 0;
    let raf = 0;
    const podeAnimar = () => fino.matches && !quieto.matches && !document.hidden;
    const parar = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const desenhar = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const off = ((deriva % PASSO) + PASSO) % PASSO;
      ctx.lineCap = 'round';
      for (let y = -PASSO + off; y < h + PASSO; y += PASSO) {
        for (let x = PASSO / 2; x < w; x += PASSO) {
          const dx = x - mx;
          const dy = y - my;
          const d = Math.hypot(dx, dy);
          const perto = Math.max(0, 1 - d / RAIO) * forca;
          const e = perto * perto;
          const px = x + (d > 0 ? dx / d : 0) * e * 14;
          const py = y + (d > 0 ? dy / d : 0) * e * 14;
          // a malha some perto das bordas laterais: nasce na borda e morre para dentro
          const t = Math.min(x / w, 1 - x / w);
          const base = (claro ? 0.2 : 0.07) + (claro ? 0.08 : 0.06) * Math.max(0, 1 - t / 0.35);
          const alfa = base + e * (claro ? 0.65 : 0.85);
          if (alfa < 0.012) continue;
          const r = 2.2 + e * 5;
          ctx.strokeStyle = `rgba(${claro ? '65, 100, 20' : '163, 230, 53'}, ${alfa.toFixed(3)})`;
          ctx.lineWidth = 1 + e * 1.2;
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate((e * Math.PI) / 4);
          ctx.beginPath();
          ctx.moveTo(-r, 0);
          ctx.lineTo(r, 0);
          ctx.moveTo(0, -r);
          ctx.lineTo(0, r);
          ctx.stroke();
          ctx.restore();
        }
      }
    };

    const quadro = () => {
      raf = 0;
      if (!podeAnimar()) return;
      mx += (ax - mx) * 0.2;
      my += (ay - my) * 0.2;
      forca += (alvoForca - forca) * 0.12;
      deriva += (alvoDeriva - deriva) * 0.14;
      desenhar();
      const assentando =
        Math.abs(ax - mx) > 0.4 ||
        Math.abs(ay - my) > 0.4 ||
        Math.abs(alvoForca - forca) > 0.01 ||
        Math.abs(alvoDeriva - deriva) > 0.2;
      if (assentando) raf = requestAnimationFrame(quadro);
    };

    const acordar = () => {
      if (!raf && podeAnimar()) raf = requestAnimationFrame(quadro);
    };

    const medir = () => {
      dpr = Math.min(1.5, devicePixelRatio || 1);
      w = innerWidth;
      h = innerHeight;
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
      desenhar();
    };

    const aoRolar = () => {
      if (!podeAnimar()) return;
      alvoDeriva = -scrollY * 0.25;
      acordar();
    };
    const aoMover = (e: PointerEvent) => {
      if (!podeAnimar()) return;
      ax = e.clientX;
      ay = e.clientY;
      if (mx < -999) {
        mx = ax;
        my = ay;
      }
      alvoForca = 1;
      acordar();
    };
    const aoSair = () => {
      alvoForca = 0;
      acordar();
    };
    const atualizarMovimento = () => {
      if (podeAnimar()) acordar();
      else parar();
    };

    medir();
    addEventListener('resize', medir, { passive: true });
    addEventListener('scroll', aoRolar, { passive: true });
    addEventListener('pointermove', aoMover, { passive: true });
    document.addEventListener('pointerleave', aoSair);
    document.addEventListener('visibilitychange', atualizarMovimento);
    quieto.addEventListener('change', atualizarMovimento);
    fino.addEventListener('change', atualizarMovimento);

    return () => {
      parar();
      removeEventListener('resize', medir);
      removeEventListener('scroll', aoRolar);
      removeEventListener('pointermove', aoMover);
      document.removeEventListener('pointerleave', aoSair);
      document.removeEventListener('visibilitychange', atualizarMovimento);
      quieto.removeEventListener('change', atualizarMovimento);
      fino.removeEventListener('change', atualizarMovimento);
    };
  }, [theme]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 h-screen w-screen"
    />
  );
}
